import { createEffect, ofType } from "@ngrx/effects";
import { EffectMetadata } from "@ngrx/effects/src/models";
import { ActionCreator } from "@ngrx/store";
import { Action, TypedAction } from "@ngrx/store/src/models";
import { Observable, of } from "rxjs";
import { catchError, map, mergeMap } from "rxjs/operators";
import { NgrxActions } from "../decorators.module";
import { addEffectMetadataEntry, DEFAULT_EFFECT_CONFIG, EffectPropertyKey } from "../utils/helpers";

type ActionCreatorOfResponseType<T> = ActionCreator<string, (props: T) => T & TypedAction<string>>;
type DecoratorFunc<TProps, TObserverResponse> = (action: TProps) => Observable<TObserverResponse>


export interface IOnObserveThenDispatchConfig<TProps, TObserverResponse> {
  forAction: ActionCreatorOfResponseType<TProps>,
  dispatch: ActionCreatorOfResponseType<TObserverResponse>,
  dispatchOnFail: ActionCreatorOfResponseType<unknown> |  ActionCreator<string, () => TypedAction<string>>
}

export function OnObserveThenDispatchEffect<TProps, TObserverResponse>(config: IOnObserveThenDispatchConfig<TProps, TObserverResponse>){

  return function <T extends Object, K extends EffectPropertyKey<T>>(
    target: T, propertyKey: K,
    descriptor: TypedPropertyDescriptor<DecoratorFunc<TProps, TObserverResponse>>) {

    const originalMethod = descriptor.value;
    if (typeof originalMethod !== 'function') {
      throw new TypeError(
        `Unexpected type ${typeof originalMethod} of property ${propertyKey}, ` +
        `expected 'function'`
      );
    }


    const effectKey = `${propertyKey}_key_$` as K

    const metadata: EffectMetadata<T> = {
      ...DEFAULT_EFFECT_CONFIG, 
      propertyName: effectKey,
    };

    let efunc: any;
    Object.defineProperty(target, effectKey, {
      get() {

        const { actions$ } = NgrxActions;

        if (actions$ === null) {
          throw new Error(`${NgrxActions.name} not connected to store!, Please ensure you are injecting NgrxHelperModule`);
        }

        const callback = originalMethod.bind(this);
        const effect = () => {
          return createEffect(
            () => actions$.pipe(
              ofType(config.forAction),
              mergeMap(action => {
                return callback(action as TProps);
              }),
              map(response => {
                  return config.dispatch(response) as Action
              }),
              catchError((error) => {
                return of(config.dispatchOnFail(error))
              })
            )
          )
        }

        if (!efunc) {
          efunc = effect();
        }
        return efunc;
      },
      set: () => { },
    });


    addEffectMetadataEntry<T>(target, metadata);

  }
  
}
